import axios from 'axios';
import {GoogleMap} from '../map/google-map-models';
import {store} from '../../store/global-store';
import {Field} from 'vee-validate';

const addressSelector = {
    props: {
        opts: {},
        geoModel: {},
        geoRadius: {},
        measureMultiplier: {},
        initialLocation: {},
        externalErrorMessages: {},
    },
    components: {
        Field: Field,
    },
    template: '#addressSelector',
    data() {
        this.geoModel.enteredByUser = this.geoModel.enteredByUser || this.geoModel.formatted;
        return {
            locationError: false,
            labels: this.opts.labels,
            address: this.geoModel || {enteredByUser: null, formatted: null},
            marker: null,
            map: null,
            userLocation: null,
            zoom: 11,
            addressEditing: false,
            circle: null,
            radius: this.geoRadius,
            radiusOptions: [],
            btnLabels: {pick: null, show: null},
            urls: null,
            isRadiusSelectOpen: false,
            isEditMode: false,
            isMapVisible: this.opts.showMap,
            isDirty: false,
            guidePopupExpanded: true,
            showGuidePopup: this.opts.showGuidePopup,
            showExternalErrors: false,
        };
    },
    computed: {
        addressPickBtnVisible() {
            return !this.isMapVisible || this.address.enteredByUser;
        },
        buttonValue() {
            return !(this.isMapVisible || this.address.formatted) ? this.btnLabels.show : this.btnLabels.pick;
        },
        isShowMapButtonVisible() {
            return this.opts.hasShowMapButton && !this.opts.hasHideMapButton && !this.isMapVisible;
        },
        isHideMapButtonVisible() {
            return this.opts.hasHideMapButton && this.isMapVisible;
        },
        isEditButtonVisible() {
            return this.address.latitude != null && this.address.longitude != null;
        },
        showExternalErrors() {
            return this.externalErrorMessages && this.externalErrorMessages.length > 0 && this.showExternalErrors;
        },
        showAddressInfo() {
            return this.address.enteredByUser && !this.showExternalErrors;
        }
    },
    watch: {
        'externalErrorMessages'() {
            this.showExternalErrors = true;
        },
        'userLocation'() {
            if (this.userLocation && this.isMapVisible) {
                this.renderMap(this.userLocation);
            }
        },
        radius() {
            this._emitAddressUpdatedEvent();
            if (!this.isMapVisible && this.radius.toString() !== this.geoRadius.toString()) {
                this.showMap();
            }
            this.paintCircleOnMap();
        },
        'geoRadius': {
            handler() {
                this.radius = this.geoRadius;
            },
            immediate: true,
        },
        'address.enteredByUser'() {
            this.isDirty = true;
            this.showExternalErrors = false;
        },
        'address.formatted'() {
            this.address.enteredByUser = this.address.formatted;
            this.$nextTick(() => this.isDirty = false);
            this.showExternalErrors = false;
        },
        'address.latitude'() {
            if (this.address.latitude != null && this.address.longitude != null) {
                this.showMap();
            }
        },
    },
    methods: {
        initDefaultValues() {
            const addressContainer = this.$refs.addressContainer;
            this.defaultLocation = JSON.parse(addressContainer.dataset['defaultLocation']);
            this.allowedCountryForSearch = addressContainer.dataset['allowedCountry'];

            if (!this.labels) {
                this.labels = {};
            }

            this.btnLabels.show = this.labels.show || addressContainer.dataset['labelShow'];
            this.btnLabels.pick = this.labels.pick || addressContainer.dataset['labelPick'];
            this.apiKey = addressContainer.dataset['apiKey'];
            this.opts.hints = this.opts.hints || JSON.parse(addressContainer.dataset['defaultHints']);

            this.urls = {
                placeDetailsUrl: addressContainer.dataset['placeDetailsUrl'],
                searchLocationUrl: addressContainer.dataset['searchLocationUrl'],
                userLocationUrl: addressContainer.dataset['userLocationUrl'],
                geocodeLocationUrl: addressContainer.dataset['geocodeLocationUrl'],
                getZoomLevelUrl: addressContainer.dataset['getZoomLevelUrl'],
            };
        },
        initMap() {
            if (!this.map) {
                this.$nextTick(() => {
                    if (!this.userLocation) {
                        this.setCurrentUserLocation();
                    }
                });
            }
        },
        renderMap(centerLocation) {
            this.map = new GoogleMap(document.getElementById('map'),
                {
                    center: {
                        lat: centerLocation.lat,
                        lng: centerLocation.lng,
                    },
                    zoom: this.zoom,
                    minZoom: 5,
                    maxZoom: 20,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.RIGHT_CENTER,
                    },
                    mapTypeControlOptions: {
                        position: google.maps.ControlPosition.TOP_CENTER,
                    },
                    mapTypeControl: false,
                    rotateControl: false,
                },
                this.opts.headers);

            this.geocoder = new google.maps.Geocoder;
            const vm = this;

            this.map.addListener('click',
                (event) => {
                    vm.placeMarker(event.latLng, true);
                });

            this.setMapInitialValues();
        },
        setMapInitialValues() {
            if (this.address.formatted && this.address.latitude && this.address.longitude) {
                if (this.radius) {
                    this.zoomMapToCircleBounds();
                } else {
                    this.setMapZoom(this.address);
                }

                this.putMarkerToCenter(new google.maps.LatLng(this.address.latitude, this.address.longitude));
            }
        },
        placeMarker(location, needSetAddress) {
            if (!this.marker) {
                this.marker = this.map.createMarker({
                    position: location,
                    icon: '/images/marker/default.svg',
                    draggable: true,
                    animation: google.maps.Animation.DROP,
                });

                const vm = this;
                this.marker.addListener('dragend',
                    (event) => {
                        vm.setMarkerAddress(event.latLng);
                    });

                this.paintCircleOnMap();
            } else {
                this.marker.resetMap();
                this.marker.setAnimation(google.maps.Animation.DROP);
                this.marker.setPosition(location);
                this.map.setMapToMarker(this.marker);
            }

            if (needSetAddress) {
                this.setMarkerAddress(location);
            }
        },
        setMarkerAddress(location) {
            const url = `${this.urls.geocodeLocationUrl}?latitude=${location.lat()}&longitude=${location.lng()}`;
            axios.get(url).then((response) => this.setAddress(response.data, location));
        },
        setCurrentUserLocation() {
            if (this.initialLocation) {
                this.userLocation = {lat: this.initialLocation.latitude, lng: this.initialLocation.longitude};
            } else {
                axios.get(this.urls.userLocationUrl).then((response) => {
                    const result = response.data;
                    if (!result.error) {
                        this.userLocation = {lat: result.latitude, lng: result.longitude};
                    } else {
                        this.userLocation = this.defaultLocation;
                    }
                });
            }
        },
        setAddress(mapData, originalLocation) {
            if (mapData) {
                this.address.formatted = mapData.formatted;
                this.address.jsonSerializedGMapData = mapData.jsonSerializedGMapData;
                this.address.enteredByUser = mapData.formatted;
                this.address.latitude = originalLocation ? originalLocation.lat() : mapData.latitude;
                this.address.longitude = originalLocation ? originalLocation.lng() : mapData.longitude;
                this.address.bounds = mapData.bounds;
                this.address.city = mapData.city;
                this.address.coordinatesProvidedByUser = originalLocation != null;


                this._emitAddressUpdatedEvent();
                this.locationError = false;
                this.$nextTick(() => this.isDirty = false);
            } else {
                this.locationError = true;
            }
        },
        putMarkerToCenter(location) {
            this.map.setCenter(location);
            this.placeMarker(location, false);
        },
        setMapZoomAndLocation(mapData) {
            if (!(mapData && mapData.latitude && mapData.longitude)) {
                this.locationError = true;
                return;
            }

            this.locationError = false;

            if (this.isMapVisible) {
                if (this.circle) {
                    this.zoomMapToCircleBounds();
                } else if (!this.radius) {
                    this.setMapZoom(mapData);
                } else {
                    // no actions required
                }
            }

            this.setAddress(mapData);

            if (mapData.latitude && mapData.longitude && this.isMapVisible) {
                this.putMarkerToCenter(new google.maps.LatLng(mapData.latitude, mapData.longitude));
            }
        },
        setMapZoom(mapData) {
            if (mapData.bounds) {
                this.fitMapToBounds(mapData.bounds);
            } else if (mapData.formatted) {
                const url = `${this.urls.getZoomLevelUrl}?address=${mapData.formatted}`;
                axios.get(url).then((response) => {
                    const result = response.data;
                    if (!result.error) {
                        this.zoom = result;
                        this.map.setZoom(result);
                    }
                });
            } else {
                // no actions required
            }
        },
        fitMapToBounds(bounds) {
            const viewPort = this.map.getViewPort(bounds);
            this.map.fitBounds(viewPort);
        },
        getLocationToShow() {
            return this.address && this.address.latitude && this.address.longitude ?
                {lat: this.address.latitude, lng: this.address.longitude} :
                this.defaultLocation;
        },
        paintCircleOnMap() {
            if (this.radius && this.marker) {
                const circleRadius = this.radius * this.measureMultiplier;
                if (!this.circle) {
                    this.circle = this.map.createCircle(this.marker._marker, {
                        strokeColor: 'rgb(247, 57, 191)',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: 'rgb(247, 57, 191)',
                        fillOpacity: 0.2,
                        center: this.marker.getPosition(),
                        radius: circleRadius, // in meters
                    });

                    this.circle.bindTo('center', 'position');
                } else {
                    this.circle.setRadius(circleRadius);
                }

                this.zoomMapToCircleBounds();
            }
        },
        zoomMapToCircleBounds() {
            if (this.circle && this.map) {
                this.map.fitBounds(this.circle.getBounds());
            }
        },
        editAddress() {
            this.isEditMode = true;
            this.$nextTick(() => {
                this.$refs.editableInput.startEdit();
            });
        },
        saveEditedAddress() {
            this._emitAddressUpdatedEvent();
            this.isEditMode = false;
        },
        showMap() {
            if (!this.isMapVisible) {
                this.isMapVisible = true;
                this.$nextTick(() => {
                    if (!this.map) {
                        const location = this.getLocationToShow();
                        this.renderMap(location);
                    }
                    this.$scrollTo(this.$el, {container: this.$el.closest('[data-scrollable-container]'), offset: 1});
                });
            }
        },
        hideMap() {
            this.isMapVisible = false;
        },
        changeGuidePopupStatus(){
          this.guidePopupExpanded = !this.guidePopupExpanded;
        },
        _autocompleteCallback(result) {
            this.setMapZoomAndLocation(result);
            this.$nextTick(() => this.isDirty = false);
        },
        _initAutocomplete() {
            store.subscribe((mutation) => {
                if (mutation.type === 'autocompleteCallback') {
                    this._autocompleteCallback(mutation.payload);
                }
            });
        },
        _emitAddressUpdatedEvent() {
            this.$emit('address-updated', this.address, this.radius);
        },
    },
    mounted() {
        this.initDefaultValues();
        this.initMap();
        this._initAutocomplete();

        if (this.radius && !this.measureMultiplier) {
            console.error('measureMultiplier is required');
        }
    },
};

function initAddressSelectorComponent(app) {
    app.component('address-selector', addressSelector);
};

export default initAddressSelectorComponent;
